
/**
 * Extracts a YouTube video ID (11 chars) from most common YouTube URL variants.
 *
 * Supported examples:
 * - https://www.youtube.com/watch?v=VIDEO_ID
 * - https://youtu.be/VIDEO_ID
 * - https://www.youtube.com/embed/VIDEO_ID
 * - https://www.youtube.com/v/VIDEO_ID
 * - https://www.youtube.com/shorts/VIDEO_ID
 * - https://m.youtube.com/watch?v=VIDEO_ID
 * - https://www.youtube-nocookie.com/embed/VIDEO_ID
 * - URLs with extra params: &t=, &list=, &si=, etc.
 * - Already-pasted IDs: VIDEO_ID
 *
 * Returns: string (videoId) or null
 */
export function getYouTubeVideoId(input) {
  if (input == null) return null;
  const str = String(input).trim();
  if (!str) return null;

  
  if (/^[a-zA-Z0-9_-]{11}$/.test(str)) return str;

  
  const scan = (s) => {
    const m = s.match(
      /(?:youtube(?:-nocookie)?\.com\/(?:.*[?&]v=|(?:embed|v|shorts|live)\/)|youtu\.be\/|img\.youtube\.com\/vi\/|i\.ytimg\.com\/vi\/)([a-zA-Z0-9_-]{11})/i
    );
    return m ? m[1] : null;
  };

  
  let url;
  
  try {
    url = new URL(str);
  } catch {
    try {
      url = new URL(`https://${str}`);
    } catch {
      return scan(str);
    }
  }

  const host = url.hostname.replace(/^www\./, "").toLowerCase();

  // 1) Standard watch URLs: ?v=VIDEO_ID
  const v = url.searchParams.get("v");
  if (v && /^[a-zA-Z0-9_-]{11}$/.test(v)) return v;

  // 2) youtu.be/VIDEO_ID
  if (host === "youtu.be") {
    const id = url.pathname.split("/").filter(Boolean)[0] || "";
    return /^[a-zA-Z0-9_-]{11}$/.test(id) ? id : null;
  }

  // 3) youtube.com/embed/ID, /v/ID, /shorts/ID, /live/ID
  if (host.endsWith("youtube.com") || host.endsWith("youtube-nocookie.com") || host === "m.youtube.com") {
    const parts = url.pathname.split("/").filter(Boolean);
    const idx = parts.findIndex((p) => ["embed", "v", "shorts", "live"].includes(p));
    if (idx !== -1 && parts[idx + 1] && /^[a-zA-Z0-9_-]{11}$/.test(parts[idx + 1])) {
      return parts[idx + 1];
    }
  }

  // 4) Thumbnail / image hosts:
  // - img.youtube.com/vi/ID/0.jpg
  // - i.ytimg.com/vi/ID/hqdefault.jpg (and similar)
  if (host === "img.youtube.com" || host === "i.ytimg.com") {
    const parts = url.pathname.split("/").filter(Boolean);
    // expected: ["vi", "ID", "0.jpg"] or ["vi", "ID", "hqdefault.jpg"]
    const viIdx = parts.indexOf("vi");
    if (viIdx !== -1 && parts[viIdx + 1] && /^[a-zA-Z0-9_-]{11}$/.test(parts[viIdx + 1])) {
      return parts[viIdx + 1];
    }
    // sometimes: /an_webp/ID/... or /vi_webp/ID/...
    const anyId = parts.find((p) => /^[a-zA-Z0-9_-]{11}$/.test(p));
    if (anyId) return anyId;
  }

  // 5) Last resort
  return scan(str);
}